import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Activity, Utensils, User, MessageCircle, Flame } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { communityService } from '../../services/communityService';
import { isChronicEnabled } from '../../config/appMode';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPaths?: string[];
  isProfile?: boolean;
}

// Tab-ul de "Activitate" — Calendar este CHRONIC-only, deci îl excludem din
// match-uri ca să nu activeze tab-ul de Activitate în FII FIT.
const activityMatchPaths = isChronicEnabled()
  ? ['/exercises', '/advanced-tracking', '/calendar', '/challenges', '/goals', '/progress-photos', '/journal']
  : ['/exercises', '/challenges', '/goals', '/progress-photos', '/journal'];

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Acasă',
    icon: Home,
    matchPaths: ['/dashboard'],
  },
  {
    path: '/exercises',
    label: 'Activitate',
    icon: Activity,
    matchPaths: activityMatchPaths,
  },
  {
    path: '/food-community',
    label: 'Retete',
    icon: Flame,
    matchPaths: ['/food-community'],
  },
  {
    path: '/nutrition',
    label: 'Nutritie',
    icon: Utensils,
    matchPaths: ['/nutrition', '/food-tracking'],
  },
  {
    path: '/profile',
    label: 'Profil',
    icon: User,
    matchPaths: ['/profile', '/settings', '/subscriptions', '/help'],
    isProfile: true,
  },
];


export default function BottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const lastScrollY = useRef(0);
  const scrollThreshold = 10; // minimum scroll distance to trigger hide/show

  // Load unread messages count - poll every 10 seconds for near real-time
  useEffect(() => {
    const loadUnread = async () => {
      try {
        const conversations = await communityService.getConversations();
        const totalUnread = conversations.reduce((sum: number, conv: any) => sum + (conv.unreadCount || 0), 0);
        setUnreadMessages(totalUnread);
      } catch (err) {
        // Silently fail - user may not have community access
      }
    };
    if (user) {
      loadUnread();
      // Refresh every 10 seconds for near real-time notifications
      const interval = setInterval(loadUnread, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Listen for instant unread count updates (dispatched when messages are sent/received)
  useEffect(() => {
    const handleUnreadUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ unreadCount: number }>;
      if (typeof customEvent.detail?.unreadCount === 'number') {
        setUnreadMessages(customEvent.detail.unreadCount);
      }
    };
    // Also listen for a simple refresh trigger
    const handleRefreshUnread = () => {
      communityService.getConversations().then(conversations => {
        const totalUnread = conversations.reduce((sum: number, conv: any) => sum + (conv.unreadCount || 0), 0);
        setUnreadMessages(totalUnread);
      }).catch(() => {});
    };
    window.addEventListener('unread-messages-update', handleUnreadUpdate);
    window.addEventListener('refresh-unread-messages', handleRefreshUnread);
    return () => {
      window.removeEventListener('unread-messages-update', handleUnreadUpdate);
      window.removeEventListener('refresh-unread-messages', handleRefreshUnread);
    };
  }, []);

  // Listen for mobile drawer open/close events from Navigation component
  useEffect(() => {
    const handleDrawerToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ open: boolean }>;
      setDrawerOpen(customEvent.detail.open);
    };
    window.addEventListener('mobile-drawer-toggle', handleDrawerToggle);
    return () => window.removeEventListener('mobile-drawer-toggle', handleDrawerToggle);
  }, []);

  // Listen for custom modal-toggle events (can be dispatched by any modal)
  useEffect(() => {
    const handleModalToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ open: boolean }>;
      setModalOpen(customEvent.detail.open);
    };
    window.addEventListener('modal-toggle', handleModalToggle);
    return () => window.removeEventListener('modal-toggle', handleModalToggle);
  }, []);

  // Auto-detect modals/popups/dialogs in the DOM using MutationObserver
  useEffect(() => {
    const checkForModals = () => {
      // Check for common modal patterns: fixed/absolute overlays, dialog elements, 
      // elements with role="dialog", common modal class names
      const modalSelectors = [
        '[role="dialog"]',
        '[role="alertdialog"]',
        'dialog[open]',
        '.modal',
        '.ReactModal__Overlay',
        '[data-modal]',
        '[data-popup]',
        // Fixed overlays with backdrop (common pattern for modals)
        '.fixed.inset-0',
        '.fixed.top-0.left-0.right-0.bottom-0',
      ];
      
      const hasModal = modalSelectors.some(selector => {
        try {
          return document.querySelector(selector) !== null;
        } catch {
          return false;
        }
      });

      // Also check for any fixed/absolute positioned elements with high z-index that look like overlays
      const fixedElements = document.querySelectorAll('.fixed');
      let hasOverlay = false;
      fixedElements.forEach(el => {
        const htmlEl = el as HTMLElement;
        const rect = htmlEl.getBoundingClientRect();
        const style = window.getComputedStyle(htmlEl);
        const zIndex = parseInt(style.zIndex) || 0;
        
        // If it's a full-screen or near-full-screen fixed element with backdrop/overlay characteristics
        // and it's not the bottom nav itself or the top nav
        if (
          rect.width >= window.innerWidth * 0.8 &&
          rect.height >= window.innerHeight * 0.3 &&
          zIndex >= 40 &&
          !htmlEl.closest('nav') &&
          !htmlEl.classList.contains('md:hidden') // not the bottom nav itself
        ) {
          // Check if it has a semi-transparent background (overlay/backdrop)
          const bg = style.backgroundColor;
          if (bg.includes('rgba') || bg.includes('0.') || htmlEl.classList.contains('bg-black/50') || 
              htmlEl.classList.contains('bg-black/40') || htmlEl.classList.contains('bg-gray-900/50') ||
              htmlEl.querySelector('[role="dialog"]') || htmlEl.querySelector('.modal')) {
            hasOverlay = true;
          }
        }
      });

      setModalOpen(hasModal || hasOverlay);
    };

    // Run check initially
    checkForModals();

    // Observe DOM changes to detect when modals are added/removed
    const observer = new MutationObserver(() => {
      // Small delay to let the DOM settle after changes
      requestAnimationFrame(checkForModals);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'open', 'role'],
    });

    return () => observer.disconnect();
  }, []);

  // Scroll direction detection - hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollY.current;

      // Only trigger if scrolled more than threshold
      if (Math.abs(diff) < scrollThreshold) return;

      if (diff > 0 && currentScrollY > 80) {
        // Scrolling DOWN & past initial area - hide
        setIsVisible(false);
      } else if (diff < 0) {
        // Scrolling UP - show
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Always show when at top of page
  useEffect(() => {
    if (window.scrollY <= 80) {
      setIsVisible(true);
    }
  }, [location.pathname]);

  // Reset visibility on route change
  useEffect(() => {
    setIsVisible(true);
    lastScrollY.current = 0;
  }, [location.pathname]);

  // Don't show bottom nav for specialists
  if (user?.userType === 'SPECIALIST') return null;

  const isActive = (item: NavItem) => {
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname.startsWith(p));
    }
    return location.pathname === item.path;
  };

  // Get profile photo URL
  const getProfilePhotoUrl = () => {
    const storedPhoto = localStorage.getItem('profilePhotoUrl');
    if (storedPhoto) {
      if (storedPhoto.startsWith('http') || storedPhoto.startsWith('data:')) {
        return storedPhoto;
      }
      return `${API_URL}${storedPhoto}`;
    }
    return null;
  };

  const profilePhotoUrl = getProfilePhotoUrl();

  // Don't show floating badge on the messages page itself
  const isOnMessagesPage = location.pathname.startsWith('/community/messages');

  return (
    <>
      {/* Floating Unread Messages Badge */}
      {unreadMessages > 0 && !isOnMessagesPage && isVisible && !drawerOpen && !modalOpen && (
        <button
          onClick={() => navigate('/community/messages')}
          className="fixed left-4 z-50 md:hidden animate-bounce-slow"
          style={{ 
            bottom: 'calc(76px + env(safe-area-inset-bottom))',
          }}
        >
          <div className="relative bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg shadow-blue-600/30 transition-all">
            <MessageCircle className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 border-2 border-white shadow-sm">
              {unreadMessages > 99 ? '99+' : unreadMessages}
            </span>
          </div>
        </button>
      )}

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>

    <nav 
      className={`fixed left-0 right-0 bg-white z-50 md:hidden transition-all duration-300 ease-in-out ${
        isVisible && !drawerOpen && !modalOpen
          ? 'translate-y-0 opacity-100 border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]' 
          : 'translate-y-[110%] opacity-0 pointer-events-none'
      }`}
      style={{ 
        bottom: 0,
        paddingBottom: 'env(safe-area-inset-bottom)' 
      }}
    >
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
        {navItems.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
                active ? 'text-blue-600' : 'text-gray-900'
              }`}
            >
              {item.isProfile && profilePhotoUrl ? (
                <div className={`w-7 h-7 rounded-full overflow-hidden border-2 ${
                  active ? 'border-blue-600' : 'border-gray-400'
                }`}>
                  <img
                    src={profilePhotoUrl}
                    alt="Profil"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <Icon className={`w-6 h-6 ${active ? 'text-blue-600' : 'text-gray-900'}`} />
              )}
              <span className={`text-[10px] mt-0.5 font-medium ${
                active ? 'text-blue-600' : 'text-gray-900'
              }`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
    </>
  );
}
